import { useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DataGridSortIcon from '../DataGridSortIcon';
import ToolTipComponent from '../../../components/GlobalComponents/ToolTipComponent';
import { COLORS } from '../../../utils/Application_Constants';
import {
    getPtsEntryInvoiceStatusTooltip,
    getPtsEntryPostedTooltip,
    isPtsEntryPosted,
    shouldShowPtsEntryInvoiceStatusIcon,
    shouldShowPtsEntryPostedIcon,
} from '../../../Admin/Configurations/processedTimesheetsConfig';

/* this function is responsible for checking if a cell is a group header and if it should display its value, and with this we avoid repeating equal values */
const renderGroupedCell = (params, renderFn, groupedBy) => {
    if (!params.value) return '';
    if (!params.row.showGroupValue) return '';

    const content = renderFn ? renderFn(params) : params.value;

    if (params.row.isChild && groupedBy === 'relatedEntryId') {
        if (params.field === 'date') {
            return <span className="child-date">{content}</span>;
        }
    }

    return content;
};

const renderEntryStatusIcon = (tooltip, testId) =>
    tooltip ? (
        <ToolTipComponent
            arrow
            title={tooltip}
            placement="top"
            slotProps={{ tooltip: { sx: { width: 'auto !important' } } }}
        >
            <InfoOutlinedIcon
                data-testid={testId}
                aria-label={tooltip}
                sx={{
                    fontSize: 16,
                    color: COLORS.LT_JOURNEY,
                    mr: 0.25,
                }}
            />
        </ToolTipComponent>
    ) : null;

const getActionsColumn = (handleClickAux) => ({
    field: 'actions',
    headerName: '',
    headerClassName: 'custom-header',
    sortable: false,
    filterable: false,
    width: 72,
    renderCell: (params) => {
        const invoiceStatusTooltip = getPtsEntryInvoiceStatusTooltip(params.row);
        const showInvoiceStatusIcon = shouldShowPtsEntryInvoiceStatusIcon(params.row.invoiceStatus);
        const postedTooltip = getPtsEntryPostedTooltip(params.row);
        const showPostedIcon = shouldShowPtsEntryPostedIcon(params.row) && !showInvoiceStatusIcon;

        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{ width: '100%' }}
            >
                {showInvoiceStatusIcon &&
                    renderEntryStatusIcon(invoiceStatusTooltip, 'entry-invoice-status-info-icon')}
                {showPostedIcon && renderEntryStatusIcon(postedTooltip, 'entry-posted-info-icon')}
                <IconButton aria-label="options" onClick={(event) => handleClickAux(event, params)}>
                    <MoreVertIcon />
                </IconButton>
            </Stack>
        );
    },
});

const StripedDataGrid = ({
    columns,
    rows = [],
    groupedBy = null,
    showOptions = false,
    onDelete,
    onEdit,
    onView,
    isLoading = false,
    initialSortModel = [],
    onSortModelChange,
    readOnly = false,
    getRowClassName: getRowClassNameProp,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const handleClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleClose = () => {
        // Keep selectedRow until the next open so menu items stay stable during the close animation
        setAnchorEl(null);
    };
    /* here I'm using useMemo to re-execute all the logic if the rows or the groupedBy change */
    const processedRows = useMemo(() => {
        /* if groupedBy is false/null/undefined the original rows will be returned but with the background color setting to interleave one by one */
        if (!groupedBy) {
            return rows.map((r, index) => ({
                ...r,
                id: r.id ?? index,
            }));
        }

        const valueToGroupClass = {};
        let groupIndex = 0;

        const grouped = {};
        /* here I group the rows according to the value that arrives in groupedBy. */
        rows.forEach((row) => {
            const groupValue = row[groupedBy];
            if (!grouped[groupValue]) {
                grouped[groupValue] = [];
            }
            grouped[groupValue].push(row);
        });

        if (groupedBy === 'relatedEntryId') return rows;

        /* here I review the previously created groups and compare the startTime values to verify which one should be first. I also check the background color for each row */
        const sortedGroups = Object.entries(grouped).map(([groupValue, groupRows]) => {
            /* here I assign the background color for each group and with the index === 0 I ensure that for each group only the value in the first row of that group is displayed. this line "const showGroupValue = !(groupValue in valueToGroupClass);" is to check if that group has already been processed */
            const showGroupValue = !(groupValue in valueToGroupClass);
            if (showGroupValue) {
                valueToGroupClass[groupValue] = groupIndex % 2 === 0 ? 'even-row' : 'odd-row';
                groupIndex++;
            }
            const groupClass = valueToGroupClass[groupValue];

            return groupRows.map((row, index) => ({
                ...row,
                showGroupValue: index === 0,
                groupClass,
            }));
        });

        return sortedGroups.flat().map((row, index) => ({
            ...row,
            id: row.id ?? index,
        }));
    }, [rows, groupedBy]);

    /* here it is validated if the column within the map matches the groupedBy (in this case date) and if that happens the function is applied to the renderCell to do the grouping and if not it returns the original column.. finally, the 'actions' column is concatenated if showOptions is true */
    const enhancedColumns = useMemo(
        () =>
            columns
                .map((col) =>
                    (groupedBy && col.field === groupedBy) || groupedBy === 'relatedEntryId'
                        ? {
                              ...col,
                              renderCell: (params) =>
                                  renderGroupedCell(params, col.renderCell, groupedBy),
                          }
                        : col
                )
                .concat(showOptions ? [getActionsColumn(handleClick)] : []),
        [columns, showOptions, groupedBy]
    );

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    minWidth: 0,
                    marginBottom: '12px',
                }}
            >
                <DataGrid
                    rows={processedRows}
                    columns={enhancedColumns}
                    disableSelectionOnClick
                    hideFooterPagination
                    disableColumnMenu
                    getRowClassName={(params) => {
                        const classNames = [];
                        if (params.row.groupClass) {
                            classNames.push(params.row.groupClass);
                        }
                        if (getRowClassNameProp) {
                            const customClassName = getRowClassNameProp(params);
                            if (customClassName) {
                                classNames.push(customClassName);
                            }
                        }
                        return classNames.join(' ');
                    }}
                    loading={isLoading}
                    initialState={{ sorting: { sortModel: initialSortModel } }}
                    className={groupedBy == null ? 'striped' : ''}
                    onSortModelChange={onSortModelChange}
                    slots={{ columnHeaderSortIcon: DataGridSortIcon }}
                    sx={{
                        '& .MuiDataGrid-overlayWrapperInner': {
                            maxHeight: 260,
                            overflow: 'hidden',
                        },
                    }}
                />
            </Box>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {onView && (
                    <MenuItem
                        onClick={() => {
                            onView(selectedRow);
                            handleClose();
                        }}
                    >
                        View
                    </MenuItem>
                )}

                {onEdit && !readOnly && !isPtsEntryPosted(selectedRow) && (
                    <MenuItem
                        onClick={() => {
                            onEdit(selectedRow);
                            handleClose();
                        }}
                    >
                        Edit
                    </MenuItem>
                )}
                {onDelete && !readOnly && !isPtsEntryPosted(selectedRow) && (
                    <MenuItem
                        onClick={() => {
                            onDelete(selectedRow);
                            handleClose();
                        }}
                    >
                        Delete
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default StripedDataGrid;
